import { Box } from "@mui/joy";
import React from "react";
import { BOTTOM_NAV_HEIGHT } from "./BottomNavBar";

export type SnapPoint = "collapsed" | "half" | "expanded";

type BottomSheetProps = {
  snapPoint: SnapPoint;
  onSnapPointChange: (snapPoint: SnapPoint) => void;
  children: React.ReactNode;
};

const HANDLE_HEIGHT = 40;
const DRAG_THRESHOLD = 50;

const getSnapPosition = (snapPoint: SnapPoint): string => {
  switch (snapPoint) {
    case "collapsed":
      return `calc(100dvh - ${BOTTOM_NAV_HEIGHT}px - ${HANDLE_HEIGHT}px)`;
    case "half":
      return "50dvh";
    case "expanded":
      return "10dvh";
  }
};

const getFallbackSnapPosition = (snapPoint: SnapPoint): string => {
  switch (snapPoint) {
    case "collapsed":
      return `calc(100vh - ${BOTTOM_NAV_HEIGHT}px - ${HANDLE_HEIGHT}px)`;
    case "half":
      return "50vh";
    case "expanded":
      return "10vh";
  }
};

export const BottomSheet = ({
  snapPoint,
  onSnapPointChange,
  children,
}: BottomSheetProps) => {
  const dragStartYRef = React.useRef<number | null>(null);
  const currentTranslateYRef = React.useRef<number>(0);
  const sheetRef = React.useRef<HTMLDivElement>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    dragStartYRef.current = e.touches[0].clientY;
    if (sheetRef.current) {
      // Disable transition during drag for responsiveness
      sheetRef.current.style.transition = "none";
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (dragStartYRef.current === null) return;
    const deltaY = e.touches[0].clientY - dragStartYRef.current;
    currentTranslateYRef.current = deltaY;
    if (sheetRef.current) {
      sheetRef.current.style.transform = `translateY(calc(${getSnapPosition(snapPoint)} + ${deltaY}px))`;
    }
  };

  const handleTouchEnd = () => {
    if (dragStartYRef.current === null) return;
    const deltaY = currentTranslateYRef.current;
    dragStartYRef.current = null;
    currentTranslateYRef.current = 0;

    if (sheetRef.current) {
      // Re-enable transition for snap animation
      sheetRef.current.style.transition = "transform 0.3s ease-out";
      sheetRef.current.style.transform = "";
    }

    // Determine new snap point based on drag direction and threshold
    if (deltaY < -DRAG_THRESHOLD) {
      // Dragged up
      if (snapPoint === "collapsed") onSnapPointChange("half");
      else if (snapPoint === "half") onSnapPointChange("expanded");
    } else if (deltaY > DRAG_THRESHOLD) {
      // Dragged down
      if (snapPoint === "expanded") onSnapPointChange("half");
      else if (snapPoint === "half") onSnapPointChange("collapsed");
    }
  };

  return (
    <Box
      ref={sheetRef}
      data-testid="mobile-bottom-sheet"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1100,
        // Use translateY for GPU-accelerated positioning
        transform: `translateY(${getSnapPosition(snapPoint)})`,
        "@supports not (height: 100dvh)": {
          transform: `translateY(${getFallbackSnapPosition(snapPoint)})`,
        },
        transition: "transform 0.3s ease-out",
        pointerEvents: snapPoint === "collapsed" ? "none" : "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Drag Handle */}
      <Box
        data-testid="bottom-sheet-handle"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        sx={(theme) => ({
          height: `${HANDLE_HEIGHT}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "grab",
          backgroundColor: theme.vars.palette.background.surface,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
          pointerEvents: "auto",
          flexShrink: 0,
        })}
      >
        <Box
          sx={(theme) => ({
            width: 36,
            height: 4,
            borderRadius: 2,
            backgroundColor: theme.vars.palette.neutral[400],
          })}
        />
      </Box>

      {/* Content */}
      <Box
        data-testid="bottom-sheet-content"
        sx={(theme) => ({
          flex: 1,
          backgroundColor: theme.vars.palette.background.surface,
          overflowY: "auto",
          overflowX: "hidden",
          // Prevent scroll when collapsed
          ...(snapPoint === "collapsed" && { overflow: "hidden" }),
        })}
      >
        {children}
      </Box>
    </Box>
  );
};
